import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ChatLink from "../../ChatLink";
import { SmallModal } from "../../UI/SmallModal/SmallModal";
import { useBookingUpdate } from "../../../hooks/useBookingUpdate";
import { markBookingAsViewed } from "../../../util/bookingViewedTracker";
import { formatBerlinDateTimeRange } from "../../../util/bookingDateTime";
import noUserImage from "../../../public/fakeUser/noUser.webp";
import { notifyErrorToast } from '../../../util/notifyActionError';

import classes from "./MainBookingDetails.module.css";
// import EquipmentContainer from "../../BookingPage/BookingDetails/EquipmentCarousel";

export const MainBookingDetails = ({ bookingData, packageData = null }) => {
    const { user } = useSelector((state) => state?.auth);
    const userId = user?.id || user?._id;
    
    const { updateBookingStatus, isLoading: isBookingUpdateLoading } = useBookingUpdate();
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isPriceExpanded, setIsPriceExpanded] = useState(false);
    // const [isBookingExpanded, setIsBookingExpanded] = useState(false);
    const [text, setText] = useState('');
    const [showExpandButton, setShowExpandButton] = useState(false);

    const [open, setOpen] = useState(false);

    // Check if booking start time has passed
    const hasBookingTimePassed = () => {
        if (!bookingData?.startDateTime) return false;
        const bookingStartTime = new Date(bookingData.startDateTime);
        const now = new Date();
        return now > bookingStartTime;
    };

    // Mark booking as viewed when component mounts or booking data changes
    useEffect(() => {
        if (bookingData?.id && bookingData?.status && userId) {
            markBookingAsViewed(bookingData.id, bookingData.status, userId);
        }
    }, [bookingData?.id, bookingData?.status, userId]);

    // Calculate booking duration in hours (decimal)
    const calculateBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return 0;
        const start = new Date(startDateTime);
        const end = new Date(endDateTime);
        const durationMs = end.getTime() - start.getTime();
        const durationHours = durationMs / (1000 * 60 * 60);
        return Math.round(durationHours * 100) / 100; // Round to 2 decimal places
    };

    // Format duration for display (e.g., "2 Stunden")
    const formatBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return "0 Stunden";
        const duration = calculateBookingDuration(startDateTime, endDateTime);
        const hours = Math.round(duration); // Round to nearest hour since only hours are used
        return `${hours} Stunden`;
    };

    const formatPrice = (value) => {
        const num = Number(value) || 0;
        if (Number.isInteger(num)) return String(num);
        return String(num).replace('.', ',');
    };

    // Reconstruct base studio price from stored totals and adjustments
    // Backend stores totalStudioPrice as (base - discount); extra fees are separate in appliedFees
    const calculateBaseStudioPrice = () => {
        if (bookingData?.totalStudioPrice == null) return null;

        const discount = Number(bookingData?.appliedDiscount) || 0;
        const surcharge = Number(bookingData?.appliedSurcharge) || 0;

        const base = Number(bookingData.totalStudioPrice) + discount - surcharge;
        return Math.max(0, base);
    };

    const getBookedEquipmentUnitPrice = (equipmentItemId) => {
        const studioPaidEquipmentItems = Array.isArray(bookingData?.Studio?.paidEquipmentItems?.items)
            ? bookingData.Studio.paidEquipmentItems.items
            : [];
        const matchedStudioItem = studioPaidEquipmentItems.find(
            (studioItem) => studioItem?.id === equipmentItemId
        );
        const price = Number(matchedStudioItem?.price);
        return Number.isFinite(price) ? price : null;
    };

    useEffect(() => {
        if (!bookingData) {
            setText('');
            setShowExpandButton(false);
            return;
        }

        const fullText = bookingData.projectDescription || '';
        const shouldShowExpand = fullText.length > 150;
        setShowExpandButton(shouldShowExpand);
        setText(shouldShowExpand ? (isExpanded ? fullText : `${fullText.slice(0, 150)}...`) : fullText);
    }, [isExpanded, bookingData?.projectDescription]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const togglePriceExpand = () => {
        setIsPriceExpanded(!isPriceExpanded);
    };

    // const toggleBookingExpand = () => {
    //     setIsBookingExpanded(!isBookingExpanded);
    // };

    const openCancelBookingModal = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);      
    };

    const handleCancelBooking = async () => {
        try {
            await updateBookingStatus(bookingData?.id, "CANCELLED");
            handleClose(); // Close modal after success
            navigate("/creator");
        } catch (error) {
            notifyErrorToast('Es gab ein Problem beim Stornieren der Buchung. Bitte versuche es später erneut.', {
              title: 'Buchung konnte nicht storniert werden',
            });
        }
    };

    // Helper function to format booking details for first message
    const formatBookingDetailsMessage = (bookingData) => {
        if (!bookingData) return "";
        const dateTime = formatBerlinDateTimeRange(bookingData.startDateTime, bookingData.endDateTime, {
            dateOptions: {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            },
            timeSuffix: '',
        });
        const startDate = dateTime?.startDate || '';
        const startTime = dateTime?.startTime || '';
        const endTime = dateTime?.endTime || '';
        
        return `Hallo! Ich schreibe bezüglich der Buchung:\n\n📅 ${startDate}\n⏰ ${startTime} - ${endTime}\n🏢 ${bookingData.Studio?.name}\n💰 €${bookingData.totalPrice}\n\nProjekt: ${bookingData.projectName}`;
    };

    if (!bookingData) {
        return (
            <div className={classes.container}>
                <div className={classes.guestInfosContainer}>
                    <div className={classes.skeletonTitle}></div>
                    <div className={classes.skeletonUserProfile}>
                        <div className={classes.skeletonAvatar}></div>
                        <div className={classes.skeletonUserInfo}>
                            <div className={classes.skeletonName}></div>
                            <div className={classes.skeletonRole}></div>
                        </div>
                    </div>
                </div>
                <div className={classes.horizontalLine}></div>
                <div className={classes.priceContainer}>
                    <div className={classes.skeletonPriceTitle}></div>
                    <div className={classes.skeletonPrice}></div>
                </div>
                <div className={classes.horizontalLine}></div>
                <div className={classes.cancellationContainer}>
                    <div className={classes.skeletonProjectTitle}></div>
                    <div className={classes.skeletonProjectText}></div>
                    <div className={classes.skeletonProjectText}></div>
                    <div className={classes.skeletonProjectText}></div>
                </div>
                <div className={classes.horizontalLine}></div>
                <div className={classes.footerContainer}>
                    <div className={classes.skeletonFooterButton}></div>
                    <div className={classes.skeletonFooterButton}></div>
                </div>
            </div>
        );
    }

    const canCancelConfirmedBooking = bookingData?.status === "CONFIRMED" && !hasBookingTimePassed();

    return (
    <div className={classes.container}>
        <div className={classes.guestInfosContainer}>
            <h2 className="titleSemibold3">Dein Gast</h2>
            <Link to={`/users/show/${bookingData?.User?.id}`} >
            <div className={classes.userProfile}>
                <div className={classes.userPicture}>
                    <img src={bookingData?.User?.userProfilePicture || noUserImage} alt='User Profile' />
                </div>
                <div className={classes.hostInnerContainer}>
                    <div className={classes.host}>
                        <span className='semiboldLarge'>{bookingData?.User?.firstName} {bookingData?.User?.lastName}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="15" viewBox="0 0 8 15" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M6.74011 8.26008L7 7.95487L6.74544 7.63101L1.68535 1.19336L1 1.80438L5.81471 7.92985L1.02575 13.5539L1.68782 14.1934L6.74011 8.26008Z" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.4"/>
                        </svg>
                    </div>
                    <span className='regularMiddle' style={{color: "#696F76"}}>
                        Gast
                    </span>
                </div>
            </div>
            </Link>
            <div className={classes.infosContainer}>
                {bookingData?.User?.location && (
                <div className={classes.info}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M1.01473 9.74238H5.12078C5.14962 8.50493 5.28402 7.32672 5.50592 6.2558H1.99344C1.43383 7.30609 1.0882 8.48768 1.01473 9.74238ZM2.61905 5.2558H5.74702C5.90402 4.68964 6.08737 4.16309 6.29377 3.68495C6.56316 3.0609 6.87711 2.50702 7.23255 2.05285C5.35339 2.57169 3.73528 3.71962 2.61905 5.2558ZM9.5 0.74707C4.25329 0.74707 0 5.00036 0 10.2471C0 15.4938 4.25329 19.7471 9.5 19.7471C14.7467 19.7471 19 15.4938 19 10.2471C19 5.00036 14.7467 0.74707 9.5 0.74707ZM9.5 1.74707C9.13138 1.74707 8.73924 1.91802 8.33768 2.31147C7.93387 2.70713 7.54732 3.30421 7.21188 4.08127C7.05672 4.44071 6.9146 4.83376 6.78781 5.2558H12.2122C12.0854 4.83376 11.9433 4.44071 11.7881 4.08127C11.4527 3.30421 11.0661 2.70713 10.6623 2.31147C10.2608 1.91802 9.86862 1.74707 9.5 1.74707ZM13.253 5.2558C13.096 4.68964 12.9126 4.16309 12.7062 3.68495C12.4368 3.0609 12.1229 2.50702 11.7674 2.05285C13.6466 2.57169 15.2647 3.71962 16.381 5.2558H13.253ZM12.4714 6.2558H6.52858C6.29645 7.30499 6.15178 8.48514 6.12106 9.74238H12.8789C12.8482 8.48514 12.7036 7.30499 12.4714 6.2558ZM13.8792 9.74238C13.8504 8.50493 13.716 7.32672 13.4941 6.2558H17.0066C17.5662 7.30609 17.9118 8.48768 17.9853 9.74238H13.8792ZM12.8792 10.7424H6.12083C6.15096 11.9988 6.29488 13.1784 6.52619 14.2275H12.4738C12.7051 13.1784 12.849 11.9988 12.8792 10.7424ZM13.4963 14.2275C13.7174 13.1568 13.8511 11.9791 13.8794 10.7424H17.9858C17.9138 11.9963 17.5699 13.1773 17.0123 14.2275H13.4963ZM12.2154 15.2275H6.78457C6.91218 15.6537 7.0554 16.0504 7.21188 16.4129C7.54732 17.1899 7.93387 17.787 8.33768 18.1827C8.73924 18.5761 9.13138 18.7471 9.5 18.7471C9.86862 18.7471 10.2608 18.5761 10.6623 18.1827C11.0661 17.787 11.4527 17.1899 11.7881 16.4129C11.9446 16.0504 12.0878 15.6537 12.2154 15.2275ZM11.7674 18.4413C12.1229 17.9871 12.4368 17.4332 12.7062 16.8092C12.9139 16.328 13.0983 15.7978 13.256 15.2275H16.3888C15.2723 16.7691 13.651 17.9212 11.7674 18.4413ZM7.23255 18.4413C6.87711 17.9871 6.56316 17.4332 6.29377 16.8092C6.08605 16.328 5.90169 15.7978 5.74403 15.2275H2.61121C3.7277 16.7691 5.34899 17.9212 7.23255 18.4413ZM1.9877 14.2275H5.50368C5.28255 13.1568 5.14885 11.9791 5.12056 10.7424H1.01419C1.08624 11.9963 1.43008 13.1773 1.9877 14.2275Z" fill="#1F1F1F"/>
                    </svg>
                    <span className="regularLarge">Lebt in {bookingData?.User?.location || ''}</span>
                </div>
                )}
                {/* <div className={classes.info}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="21" viewBox="0 0 23 21" fill="none">
                        <path d="M16.5589 14.5762L17.3363 19.1345L11.5 14.9064L5.66368 19.1345L6.44114 14.5762L6.69675 13.1065L6.73935 12.8296L6.537 12.6379L2 8.22868L8.28363 7.31276L8.56054 7.27016L8.67769 7.0252L11.5 1.34863L14.3223 7.0252L14.4395 7.27016L14.7164 7.31276L21 8.22868L16.463 12.6379L16.2607 12.8296L16.3033 13.1065L16.5589 14.5762Z" stroke="#1F1F1F"/>
                    </svg>
                    <span className={`regularLarge ${classes.underlined}`}>Bewertungen: Keine Bewertung</span>
                </div>
                <div className={classes.info}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                        <path d="M13.5 7.82655L13.3865 7.94164L8.02888 13.4742L5.5 10.9093L6.30243 10.0955L8.02888 11.8465L12.6895 7.0127L13.5 7.82655Z" fill="black"/>
                        <circle cx="9.5" cy="10.2471" r="9" stroke="#1F1F1F"/>
                    </svg>
                    <span className={`regularLarge ${classes.underlined}`}>Bewertungen: Keine Bewertung</span>
                </div> */}
                {/* <div className={classes.info}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                        <path d="M17.5827 18.2496H1.41734C0.637644 18.2496 0 17.6124 0 16.8263V2.81448C0 2.0283 0.637644 1.39111 1.41734 1.39111H17.5827C18.3624 1.39111 19 2.03147 19 2.81448V16.8263C19 17.6124 18.3624 18.2496 17.5827 18.2496ZM1.41734 2.44041C1.21216 2.44041 1.04486 2.60843 1.04486 2.81448V16.8263C1.04486 17.0323 1.21216 17.2003 1.41734 17.2003H17.5827C17.7878 17.2003 17.9551 17.0323 17.9551 16.8263V2.81448C17.9551 2.60843 17.7878 2.44041 17.5827 2.44041H1.41734Z" fill="#1F1F1F"/>
                        <path d="M10.0214 0.24707H8.97656V3.46471H10.0214V0.24707Z" fill="#1F1F1F"/>
                        <path d="M14.4785 0.24707H13.4336V3.46471H14.4785V0.24707Z" fill="#1F1F1F"/>
                        <path d="M5.57611 0.24707H4.53125V3.46471H5.57611V0.24707Z" fill="#1F1F1F"/>
                    </svg>
                    <span className="regularLarge">Seit November 2024 auf stu</span>
                </div> */}
            </div>
            <ChatLink 
                userId={bookingData?.User?.id}
                studioId={bookingData?.Studio?.id}
                studioName={bookingData?.Studio?.StudioName}
                studioImage={bookingData?.Studio?.image}
                className={`${classes.contactButton} semiboldSmall`}
                firstMessage={formatBookingDetailsMessage(bookingData)}
                creatorNavBar={false}
                chatRoomId={bookingData?.chatRoomID}
                bookingId={bookingData?.id}
            >
                Nachricht schreiben
            </ChatLink>
        </div>
        <div className={classes.horizontalLine}></div>
        <div className={`${classes.priceContainer} ${isPriceExpanded ? classes.expanded : ''}`}>
            {isPriceExpanded && (() => {
                const duration = calculateBookingDuration(bookingData?.startDateTime, bookingData?.endDateTime);
                const baseStudioPrice = calculateBaseStudioPrice();
                const totalStudioPrice = Number(bookingData?.totalStudioPrice) || 0;
                const appliedDiscount = Number(bookingData?.appliedDiscount) || 0;
                const appliedSurcharge = Number(bookingData?.appliedSurcharge) || 0;
                const appliedFees = Array.isArray(bookingData?.appliedFees) ? bookingData.appliedFees : [];
                const totalEquipmentPrice = Number(bookingData?.totalEquipmentPrice) || 0;
                const creativePackages = Array.isArray(bookingData?.creativePackages) ? bookingData.creativePackages : [];
                const bookedEquipment = Array.isArray(bookingData?.bookedEquipment) ? bookingData.bookedEquipment : [];
                const hasCompleteEquipmentPricing = bookedEquipment.length > 0
                    && bookedEquipment.every(
                        (item) => getBookedEquipmentUnitPrice(item?.equipmentItemId) != null
                    );

                const effectiveBaseStudioPrice = baseStudioPrice != null ? baseStudioPrice : totalStudioPrice;
                const studioHourly = duration > 0 && effectiveBaseStudioPrice
                    ? Math.round((effectiveBaseStudioPrice / duration) * 100) / 100
                    : bookingData?.Studio?.pricePerHour || null;

                // Try to derive a more descriptive discount label from studio.discountRules
                const discountRules = bookingData?.Studio?.discountRules || [];
                let discountLabel = 'Rabatt';
                if (appliedDiscount > 0 && discountRules.length && totalStudioPrice > 0) {
                    const baseForRule = totalStudioPrice + appliedDiscount; // base before discount
                    const matchingRule = discountRules.find((rule) => {
                        const minHours = Number(rule?.minHours) || 0;
                        if (duration < minHours) return false;
                        const value = Number(rule?.discountValue) || 0;
                        if (!value) return false;
                        let expected = 0;
                        if (rule?.discountType === 'PERCENTAGE') {
                            expected = Math.round((baseForRule * value) / 100);
                        } else if (rule?.discountType === 'ABSOLUTE') {
                            expected = value;
                        } else {
                            return false;
                        }
                        return Math.abs(expected - appliedDiscount) <= 1; // allow small rounding diff
                    });

                    if (matchingRule) {
                        const value = Number(matchingRule.discountValue) || 0;
                        const minHours = Number(matchingRule.minHours) || 0;
                        const valueLabel =
                            matchingRule.discountType === 'PERCENTAGE'
                                ? `${value}%`
                                : `${formatPrice(value)} €`;
                        discountLabel = `Rabatt (${valueLabel}${minHours ? ` ab ${minHours} Stunden` : ''})`;
                    }
                }

                return (
                    <div className={classes.priceBreakdown}>
                        {/* Studio basis price */}
                        {effectiveBaseStudioPrice > 0 && (
                            <div className={classes.breakdownItem}>
                                <span className="regularLarge">
                                    {bookingData?.packageName
                                        ? 'Paketpreis'
                                        : studioHourly
                                            ? `${formatPrice(studioHourly)} € x ${duration} Stunden`
                                            : `€${bookingData?.Studio?.pricePerHour} x ${formatBookingDuration(bookingData?.startDateTime, bookingData?.endDateTime)}`
                                    }
                                </span>
                                <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                    €{formatPrice(effectiveBaseStudioPrice)}
                                </span>
                            </div>
                        )}

                        {/* Rabatt */}
                        {appliedDiscount > 0 && (
                            <div className={classes.breakdownItem}>
                                <span className="regularLarge">{discountLabel}</span>
                                <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                    -€{formatPrice(appliedDiscount)}
                                </span>
                            </div>
                        )}

                        {/* Wochenendaufschlag */}
                        {appliedSurcharge > 0 && (
                            <div className={classes.breakdownItem}>
                                <span className="regularLarge">Wochenendaufschlag</span>
                                <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                    +€{formatPrice(appliedSurcharge)}
                                </span>
                            </div>
                        )}

                        {/* Extra Gebühren */}
                        {appliedFees.map((fee, index) => (
                            <div className={classes.breakdownItem} key={fee?.name || index}>
                                <span className="regularLarge">{fee?.name || 'Gebühr'}</span>
                                <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                    +€{formatPrice(Number(fee?.amount) || 0)}
                                </span>
                            </div>
                        ))}

                        {/* Creative-Pakete */}
                        {creativePackages.map((pkg, index) => {
                            const quantity = Number(pkg?.quantity) || 0;
                            const totalPrice = Number(pkg?.price) || 0;

                            if (!totalPrice) return null;

                            const unitPrice = quantity > 0 ? totalPrice / quantity : null;

                            return (
                                <div className={classes.breakdownItem} key={pkg?.packageID || pkg?.packageName || index}>
                                    <span className="regularLarge">
                                        {pkg?.packageName || 'Creative-Paket'}
                                        {unitPrice
                                            ? `: ${formatPrice(unitPrice)} € x ${quantity} Stunden`
                                            : ''}
                                    </span>
                                    <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                        +€{formatPrice(totalPrice)}
                                    </span>
                                </div>
                            );
                        })}

                        {/* Equipment */}
                        {bookedEquipment.length > 0 && (
                            <>
                                {bookedEquipment.map((item, index) => (
                                    <div className={classes.breakdownItem} key={item?.equipmentItemId || item?.name || index}>
                                        <span className="regularLarge">
                                            {item?.name || 'Equipment'} x {item?.quantity || 1}
                                        </span>
                                        {(() => {
                                            const quantity = Number(item?.quantity) || 1;
                                            const unitPrice = getBookedEquipmentUnitPrice(item?.equipmentItemId);
                                            if (unitPrice == null) return null;
                                            return (
                                                <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                                    +€{formatPrice(unitPrice * quantity)}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                ))}
                                {totalEquipmentPrice > 0 && !hasCompleteEquipmentPricing && (
                                    <div className={classes.breakdownItem}>
                                        <span className="regularLarge">Equipment gesamt</span>
                                        <span className="regularLarge" style={{whiteSpace: 'nowrap'}}>
                                            +€{formatPrice(totalEquipmentPrice)}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );
            })()}
            <div className={classes.priceMainContent}>
                <div className={classes.priceInnerContainer}>
                    <h2 style={{marginBottom: "0px"}} className={`${classes.cancellationTitle} semiboldMiddle`}>Gesamtbetrag</h2>
                    <button onClick={togglePriceExpand} className={classes.priceDetails}>
                        {isPriceExpanded ? 'Weniger anzeigen' : 'Details sehen'}
                    </button>
                </div>
                <p className={`${classes.price} titleSemibold3`}>€{formatPrice(bookingData?.totalPrice || 0)}</p>
            </div>
        </div>
        {packageData && (
            <>
                <div className={classes.horizontalLine}></div>
                <h3 className="semiboldMiddle">Kreativ-Service</h3>
                <Link to={`/members/${packageData?.creativeID}/services`} className={classes.serviceItem}>
                    <div className={classes.serviceItemImage}>
                        {(packageData?.processedPortfolioImages?.[0] || packageData?.firstPortfolioImage || packageData?.portfolioImages?.[0]) && (
                            <img 
                                src={packageData?.processedPortfolioImages?.[0] || packageData?.firstPortfolioImage || packageData?.portfolioImages?.[0]} 
                                alt={packageData?.name} 
                            />
                        )}
                    </div>
                    <div className={classes.serviceItemText}>
                        <span className="semiboldMiddle">{packageData?.name}</span>
                        <span className="regularMiddle">{packageData?.pricePerHour || packageData?.price} € pro Stunde</span>
                        <span style={{color: "var(--dark-grey-4)", marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                        <span className={`regularMiddle ${classes.serviceDescription}`}>{packageData?.description}</span>
                        {packageData?.deliverables?.[0] && (
                            <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                                    <g clipPath="url(#clip0_15597_28902)">
                                        <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                        <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                        <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                        <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_15597_28902">
                                            <rect width="21.8118" height="15.0954" fill="white"/>
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span className="regularXss">{packageData?.deliverables[0]}</span>
                            </div>
                        )}
                    </div>
                </Link>
            </>
        )}
        <div className={classes.horizontalLine}></div>
        <div className={classes.cancellationContainer}>
            <h2 className={`${classes.cancellationTitle} semiboldMiddle`}>Über das Projekt</h2>
            <h3 className='regularLarge'>{bookingData?.projectName}</h3>
            <p onClick={toggleExpand} className={classes.cancellationText}>
                {text}
            </p>
            {showExpandButton && (
                <button onClick={toggleExpand} className={`${classes.readMoreButton} semiboldSmall`}>
                    {isExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                </button>
            )}
        </div>
        {/* <div className={classes.horizontalLine}></div> */}
        {/* <EquipmentContainer /> */}
        {/* <div className={classes.horizontalLine}></div> */}
        {/* <div className={classes.cancellationContainer}>
            <h2 className={`${classes.cancellationTitle} semiboldMiddle`}>Stornierungsbedingungen</h2>
            <h3 className='regularLarge'>Sehr flexibel</h3>
            {bookingData?.cancellationConditions && (
                <p onClick={toggleBookingExpand} className={classes.cancellationText }>
                    {bookingData.cancellationConditions}
                </p>
            )}
            <button onClick={toggleBookingExpand} className={`${classes.readMoreButton} semiboldSmall`}>
                {isBookingExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
            </button>
        </div> */}
        <div className={classes.horizontalLine}></div>
        <footer 
            className={classes.footerContainer} 
            style={{justifyContent: canCancelConfirmedBooking ? "space-between" : "flex-end"}}
        >
            {canCancelConfirmedBooking && (
            <div className={classes.footerInnerContainer}>
                {/* <button className={`${classes.footerButton} semiboldSmall`}>Änderung anfragen</button> */}
                {/* <div className={classes.smallLine}></div> */}
                <button onClick={openCancelBookingModal} className={`${classes.footerButton} semiboldSmall`}>Stornieren</button>
            </div>
            )}
            <Link to="/kontakt" className={`${classes.supportButton} semiboldSmall`}>Kundensupport anfragen</Link>
        </footer>
            <SmallModal 
                fromBookingDetails={true} 
                open={open} 
                onBackdropClick={isBookingUpdateLoading ? undefined : handleClose} 
                className={classes.deleteModal}
            >
                <div className={classes.modalContent}>
                    <h2 className='titleSemibold3'>Buchung stornieren?</h2>
                    <div 
                        onClick={isBookingUpdateLoading ? undefined : handleClose} 
                        className={classes.closeButton}
                        style={{ cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer', opacity: isBookingUpdateLoading ? 0.5 : 1 }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                        </svg>
                    </div>
                    <p className={classes.modalText}>Mit dieser Auswahl stornierst du diese Buchung.</p>
                    <div className={classes.modalButtons}>
                        <button 
                            className="mediumLarge" 
                            onClick={handleCancelBooking}
                            disabled={isBookingUpdateLoading}
                            style={{
                                backgroundColor: isBookingUpdateLoading ? 'var(--light-grey-2)' : 'var(--black)', 
                                color: isBookingUpdateLoading ? 'var(--dark-grey)' : 'var(--white)',
                                cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer',
                                opacity: isBookingUpdateLoading ? 0.6 : 1
                            }}
                        >
                            {isBookingUpdateLoading ? 'Wird storniert...' : 'Buchung stornieren'}
                        </button>
                        <button 
                            className="mediumLarge" 
                            onClick={handleClose}
                            disabled={isBookingUpdateLoading}
                            style={{backgroundColor: 'var(--light-grey-2)', border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            </SmallModal>
        </div>
    );
};

export default MainBookingDetails;
